import _ from "lodash";
import ordinal from "ordinal";

import { User } from "prisma/cm/client";

import {
  AnyBenchmark,
  BenchmarkIdentifier,
  PercentileCurvePoints,
  UserBenchmarkSalarycom,
} from "./types";
import { annualizeCashValue } from "../compensation";
import { EMPLOYMENT_SUBTYPE_FULL_TIME } from "../compensation/employment/constants";
import { EmploymentSubType } from "../compensation/employment/types";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { getCurrencyOffInstance } from "../currency/getCurrencyField";
import { FieldProposal } from "../fields/types";
import { ReviewExchangeRates } from "../review/types";

export function splitToFamilySubfamily(code: string) {
  const [family, subFamily] = code.split(".");
  return {
    family,
    subFamily,
  };
}

export function joinFamilySubfamily({ family, subFamily }: { family: string; subFamily: string }) {
  return `${family}.${subFamily}`;
}

export const MIN_PERCENTILE = 0.1;
export const MAX_PERCENTILE = 99.9;

export function formatPercentileForAnalytics(percentile: number | null | undefined): string {
  const originalPercentile = percentile ?? 0;

  if (originalPercentile === MIN_PERCENTILE) {
    return `< 1st`;
  } else if (originalPercentile === MAX_PERCENTILE) {
    return `> 99th`;
  } else {
    return ordinal(Math.round(originalPercentile));
  }
}

export function getBenchmarkDataFromAnyBenchmark(benchmark: AnyBenchmark) {
  //@ts-expect-error TS2339
  const payPointData = benchmark?.data?.salary ?? benchmark?.data;

  if (_.isEmpty(payPointData)) {
    return null;
  }
  return payPointData;
}

export function getBenchmarkPercentile({
  value,
  curvePoints,
}: {
  value: number;
  curvePoints: PercentileCurvePoints;
}): number {
  const percentiles: number[] = [];

  Object.keys(curvePoints).forEach((key) => {
    //@ts-expect-error TS2339
    if (!_.isNil(curvePoints[key as keyof PercentileCurvePoints])) {
      percentiles.push(Number(key));
    }
  });

  const values = percentiles
    .map((p) => curvePoints[p as keyof PercentileCurvePoints])
    .filter((v) => !_.isNil(v) && _.isNumber(v)) as number[];

  if (!values.length) {
    return 0;
  }

  if (value <= values[0]) {
    return MIN_PERCENTILE;
  }

  if (value >= values[values.length - 1]) {
    return MAX_PERCENTILE;
  }

  for (let i = 0; i < values.length - 1; i++) {
    if (value >= values[i] && value <= values[i + 1]) {
      const v1 = values[i];
      const v2 = values[i + 1];
      const p1 = percentiles[i];
      const p2 = percentiles[i + 1];

      return p1 + ((value - v1) * (p2 - p1)) / (v2 - v1);
    }
  }

  throw new Error("Unable to calculate percentile");
}

export function getProposalBenchmarkPercentile<T extends Pick<FieldProposal, "owner" | "raise">>(
  proposal: T,
) {
  const user = proposal.owner;

  if (!user?.benchmark?.data || _.isEmpty(user.benchmark.data)) {
    return null;
  }

  const newSalary = (user.salary ?? 0) + (proposal.raise ?? 0);
  const annualizedNewSalary = annualizeCashValue({
    value: newSalary,
    incomeFrequency: user.salaryIncomeFrequency,
    employmentSubType: (user.employmentSubType ??
      EMPLOYMENT_SUBTYPE_FULL_TIME) as EmploymentSubType,
  });

  if (_.isEmpty(user.benchmark?.data)) {
    return null;
  }

  const payPointData = getBenchmarkDataFromAnyBenchmark(user.benchmark!);

  return getBenchmarkPercentile({
    value: annualizedNewSalary,
    curvePoints: payPointData,
  });
}

export function getValueFromPercentile({
  percentile,
  curvePoints,
}: {
  percentile: number;
  curvePoints: PercentileCurvePoints;
}): number {
  const percentiles: number[] = [];

  const validCurvePoints = Object.keys(curvePoints).filter((key) => {
    //@ts-expect-error TS2339
    return !_.isNil(curvePoints[key as keyof PercentileCurvePoints]);
  });

  if (!validCurvePoints.some((key) => Number(key) === percentile)) {
    return 0;
  }

  Object.keys(curvePoints).forEach((key) => {
    //@ts-expect-error TS2339
    if (!_.isNil(curvePoints[key as keyof PercentileCurvePoints])) {
      percentiles.push(Number(key));
    }
  });

  const values = percentiles
    .map((p) => curvePoints[p as keyof PercentileCurvePoints])
    .filter((v) => !_.isNil(v) && _.isNumber(v)) as number[];

  if (!values.length) {
    return 0;
  }

  if (percentile <= MIN_PERCENTILE) {
    return values[0];
  }

  if (percentile >= MAX_PERCENTILE) {
    return values[values.length - 1];
  }

  for (let i = 0; i < percentiles.length - 1; i++) {
    if (percentile >= percentiles[i] && percentile <= percentiles[i + 1]) {
      const p1 = percentiles[i];
      const p2 = percentiles[i + 1];
      const v1 = values[i];
      const v2 = values[i + 1];

      return v1 + ((percentile - p1) * (v2 - v1)) / (p2 - p1);
    }
  }

  throw new Error("Unable to calculate value from percentile");
}

export function formatBenchmark({
  percentile,
  curvePoints,
}: {
  percentile: number | null | undefined;
  curvePoints: PercentileCurvePoints;
}) {
  if (_.isNaN(percentile) || _.isNil(percentile)) {
    return null;
  }

  if (percentile === MIN_PERCENTILE) {
    const knownPercentiles = Object.keys(curvePoints ?? {}).map(_.toNumber);

    const minimumPercentile = _.min(knownPercentiles) ?? 0;

    return `< ${ordinal(minimumPercentile)}`;
  } else if (percentile === MAX_PERCENTILE) {
    const knownPercentiles = Object.keys(curvePoints ?? {}).map(_.toNumber);

    const maximumPercentile = _.max(knownPercentiles) ?? 0;

    return `> ${ordinal(maximumPercentile)}`;
  }

  return ordinal(Math.round(percentile));
}

export function expandLevels(level: string) {
  const match = level.match(/([A-Za-z]+)(\d+)/);
  if (!match) {
    throw new Error("Invalid level format");
  }

  const [, prefix, number] = match;
  const num = parseInt(number, 10);

  if (num <= 1) {
    return [`${prefix}${num}`, `${prefix}${num + 2}`, `${prefix}${num + 1}`];
  }

  return [`${prefix}${num}`, `${prefix}${num - 1}`, `${prefix}${num + 1}`];
}

export function getBenchmarkId(benchmark: BenchmarkIdentifier) {
  return `${benchmark.level}-${benchmark.subFamilyCode}-${benchmark.geo}-${benchmark.dataSet}`;
}

export function getUserBenchmarkPercentile(user: User) {
  if (_.isEmpty(user.benchmark)) {
    return null;
  }

  if (_.isNil(user.salary)) {
    return null;
  }

  const benchmarkData = user.benchmark.data;

  if (_.isEmpty(benchmarkData) || _.isNil(benchmarkData)) {
    return null;
  }

  const annualizedSalary = annualizeCashValue({
    value: user.salary,
    incomeFrequency: user.salaryIncomeFrequency,
    employmentSubType: (user.employmentSubType ??
      EMPLOYMENT_SUBTYPE_FULL_TIME) as EmploymentSubType,
  });

  const payPointData = getBenchmarkDataFromAnyBenchmark(user.benchmark!);

  const returnValue = getBenchmarkPercentile({
    value: annualizedSalary,
    curvePoints: payPointData,
  });

  return _.isNaN(returnValue) ? null : returnValue;
}

export function getCostToMarketForUser(user: User, targetPercentile: number) {
  if (_.isNil(user.salary) || !user.benchmark || !user.benchmark.data) {
    return null;
  }

  const payPointData = getBenchmarkDataFromAnyBenchmark(user.benchmark!);

  const targetBenchmarkInUserCurrency = getValueFromPercentile({
    percentile: targetPercentile,
    curvePoints: payPointData,
  });

  const annualizedUserSalary = annualizeCashValue({
    value: user.salary,
    incomeFrequency: user.salaryIncomeFrequency,
    employmentSubType: (user.employmentSubType ??
      EMPLOYMENT_SUBTYPE_FULL_TIME) as EmploymentSubType,
  });

  const isUserUnderTarget = annualizedUserSalary < targetBenchmarkInUserCurrency;

  if (isUserUnderTarget) {
    return targetBenchmarkInUserCurrency - annualizedUserSalary;
  }

  return null;
}

export function getCostToMarket(
  employees: User[],
  opts: {
    exchangeRates: ReviewExchangeRates;
    targetCurrency: string;
    targetPercentile: number;
  },
) {
  const costToTarget = _.sumBy(employees, (e) => {
    const costToMarket = getCostToMarketForUser(e, opts.targetPercentile);

    if (_.isNil(costToMarket)) {
      return 0;
    }

    const costToMarketInTargetCurrency = getConvertedCurrency({
      value: costToMarket,
      options: {
        sourceCurrency: getCurrencyOffInstance(e),
        targetCurrency: opts.targetCurrency,
        exchangeRates: opts.exchangeRates,
      },
    });

    return costToMarketInTargetCurrency.raw;
  });

  return costToTarget;
}

export function userIsBelowPercentile(user: User, percentile: number): boolean {
  const userBenchmarkPercentile = getUserBenchmarkPercentile(user);
  return !_.isNil(userBenchmarkPercentile) && userBenchmarkPercentile < percentile;
}

export function userIsAbovePercentile(user: User, percentile: number): boolean {
  const userBenchmarkPercentile = getUserBenchmarkPercentile(user);
  return !_.isNil(userBenchmarkPercentile) && userBenchmarkPercentile > percentile;
}

export function isAssignmentFullyMapped(assignment: BenchmarkIdentifier) {
  return assignment.level && assignment.subFamilyCode && assignment.geo;
}

export function isAssigmentFullyMappedSalarycom(assignment: UserBenchmarkSalarycom) {
  return assignment.level && assignment.family && assignment.geo;
}
